import { component, computed, observable, subscribe } from 'knockout-decorators'

import { EventSaleStatus, Offer, OfferAvailability } from '@tixa/schema'
import type { EventConfig } from '@tixa/schema'
import { EventState } from '~/store/event'
import { ErrorReporter, Tracker, Translations } from '~/utils'

import ArrowIcon from '~/images/arrow-right.svg?raw'
import CodeIcon from '~/images/code.svg?raw'
import CouponIcon from '~/images/coupon.svg?raw'

import './ticket-types'
import source from './offer-list.html?raw'

@component('offer-list', source)
export class OfferList {
  @observable public couponOffersLoading = false
  @observable public couponInput = ''

  @observable public codeOffersLoading = false
  @observable public codeInput = ''

  @observable public errorMessage = ''

  private eventId: string
  private eventState: EventState = EventState.get()

  @computed
  get validCoupons(): string[] {
    return this.eventState.specialOffers.validCoupons
  }

  @computed
  get couponChips(): string[] {
    return this.eventState.specialOffers.couponChips
  }
  set couponChips(value: string[]) {
    this.eventState.specialOffers.couponChips.mutate(() => {
      this.eventState.specialOffers.couponChips.removeAll()
      this.eventState.specialOffers.couponChips.push(...value)
    })
  }

  @computed
  get invalidCoupons(): string[] {
    return this.eventState.specialOffers.invalidCoupons
  }

  @computed
  get codeApplied(): string {
    return this.eventState.specialOffers.codeApplied
  }

  @computed
  get eventConfig(): EventConfig | null {
    const config = this.eventState.eventConfigById(this.eventId)
    if (!config) {
      return null
    }
    return config
  }

  @computed
  get eventOffers(): Offer[] {
    return this.eventState.offersByEventId(this.eventId)
  }

  @computed
  get name(): string | undefined {
    return this.eventConfig?.name
  }

  @computed
  get isEmpty(): boolean {
    return this.eventOffers.length === 0
  }

  @computed
  get hasSeatmap(): boolean | undefined {
    return this.eventConfig?.hasSeatmap
  }

  @computed
  get isSeatmapOpen(): boolean {
    return this.eventState.reservationStates[this.seatmapId]?.isSeatmapOpen
  }

  @computed
  get seatmapId(): string {
    return this.eventConfig?.hasSeatmap ? this.eventConfig.seatMapID ?? '' : ''
  }

  @computed
  get isSoldout(): boolean {
    return (
      this.eventConfig?.saleStatus !== EventSaleStatus.onsale &&
      this.codeApplied.length === 0
    )
  }

  @computed
  get soldoutMessage(): string {
    return this.isSoldout
      ? Translations['sale' + this.eventConfig?.saleStatus]
      : ''
  }

  @computed
  get hasCode(): boolean {
    return (
      this.eventConfig?.saleStatus === EventSaleStatus.codeonly &&
      this.codeApplied.length === 0
    )
  }

  @computed
  get hasCoupon(): boolean | undefined {
    return this.eventConfig?.hasCoupons
  }

  @computed
  get couponPlaceholder(): string {
    return this.eventConfig?.couponType || 'couponcode'
  }

  @computed
  get seatmapGroup1(): string {
    return this.eventConfig?.seatMapGroup1
      ? Translations[this.eventConfig.seatMapGroup1]
      : Translations.seats
  }

  @computed
  get seatmapGroup2(): string {
    return this.eventConfig?.seatMapGroup2
      ? Translations[this.eventConfig.seatMapGroup2]
      : Translations.standings
  }

  private couponIcon = CouponIcon
  private codeIcon = CodeIcon
  private arrowIcon = ArrowIcon

  constructor(private params: { eventID: string }) {
    this.eventId = this.params.eventID.toString()
    this.trackViewedOffers(this.eventOffers)
    subscribe<string>(
      () => this.couponInput,
      (newInput) => {
        if (newInput.endsWith(' ')) {
          this.convertCouponInput()
        }
      }
    )
  }

  private removeCoupon(code: string) {
    if (this.couponChips.includes(code)) {
      this.couponChips = this.couponChips.filter((chip) => chip !== code)
    }
  }

  private convertCouponInput() {
    if (this.couponInput.length === 0) {
      return
    }
    if (
      !this.eventState.events.some((ev) => ev.hasSeatmap) &&
      this.couponChips.length === 1
    ) {
      this.couponInput = ''
      return
    }
    if (this.couponChips.length === 10) {
      this.couponInput = ''
      return
    }
    let newCoupon = ''
    if (this.couponInput.endsWith(' ')) {
      newCoupon = this.couponInput.split(' ')[0]
    } else {
      newCoupon = this.couponInput
    }
    if (newCoupon.length > 0 && !this.couponChips.includes(newCoupon)) {
      this.couponChips.push(newCoupon)
    }
    this.couponInput = ''
  }

  private couponFieldInput(a, e: KeyboardEvent) {
    if (
      e.key === 'Backspace' &&
      this.couponInput.length === 0 &&
      this.couponChips.length > 0
    ) {
      this.couponChips.pop()
    } else if (e.key === 'Enter') {
      if (this.couponInput.length === 0) {
        e.preventDefault()
        e.cancelBubble = true
        return false
      }
      this.convertCouponInput()
      if (this.couponChips.length > 0) {
        this.applyCoupon()
      }
    }
  }
  private chipInput(code: string, e: KeyboardEvent) {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      this.removeCoupon(code)
    }
  }

  private trackViewedOffers(offerList: Offer[]) {
    try {
      const availableTickets = offerList.filter(
        (offer) => offer.availability === OfferAvailability.InStock
      )
      if (!this.eventState.isMultiEvent && availableTickets.length === 1) {
        availableTickets[0].eligibleQuantity.value = 1
      }
      if (Tracker.viewed) {
        Tracker.viewed(availableTickets)
      }
    } catch (error) {
      ErrorReporter.exception(error as Error)
    }
  }

  private async applyCoupon() {
    this.convertCouponInput()
    if (this.couponChips.length === 0) {
      return
    }
    this.couponOffersLoading = true
    const couponType = this.eventConfig?.couponType || 'coupon'
    if (this.couponChips.length === 1) {
      await this.eventState.specialOffers.getCouponOffers(
        this.eventId,
        this.couponChips[0],
        couponType
      )
    } else {
      await this.eventState.specialOffers.getMultipleCouponOffers(
        this.eventId,
        this.couponChips,
        couponType
      )
    }
    this.couponOffersLoading = false
  }

  private async applyCode() {
    if (this.codeInput.length > 0) {
      this.codeOffersLoading = true
      await this.eventState.specialOffers.getCodeOffers(
        this.eventId.toString(),
        this.codeInput
      )
      this.codeInput = ''
      this.codeOffersLoading = false
    }
  }
}
